import React, { useMemo, useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import CourseCard from "../cards/CourseCard";
import { getSeeCoursesByType } from "../../../services/coursesApi";
import InfiniteScroll from "react-infinite-scroller";
import { useInfiniteQuery } from "react-query";
import NewCourseCardShimmer from "../shimmers/NewCourseCardShimmer";
import MixpanelManager from "../../../constants/MixpanelManager";
import { MixpanelEvents } from "../../../constants/MixpanelEvents";

function getGridColumnCount() {
  if (typeof window === "undefined") return 2;
  const width = window.innerWidth;
  if (width >= 2200) return 3;
  if (width > 991) return 2;
  return 1;
}

function useGridColumns() {
  const [columns, setColumns] = useState(getGridColumnCount);

  useEffect(() => {
    const handleResize = () => {
      setColumns(getGridColumnCount());
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return columns;
}

const SavedCourses = ({ count, type }) => {
  const user = useMemo(() => JSON.parse(localStorage.getItem("SplashData")), []);
  const user_id = user?.user?.id;
  const columns = useGridColumns();
  const initialShimmerCount = count || (columns === 1 ? 4 : columns * 2);
  const loaderShimmerCount = columns === 1 ? 2 : columns * 2;

  // Identify user if not already identified
  useEffect(() => {
    if (user?.user) {
      console.log('🔍 Identifying User in Saved Courses');
      MixpanelManager.identify(
        user.user.id,
        user.user.email,
        user.user.name,
        user.user.profile_image
      );
      console.log('✅ User Identified in Saved Courses');
    }
  }, [user?.user]);

  // Track savedCourses event on component mount
  useEffect(() => {
    console.log('📚 Saved Courses Event - Tracking');
    MixpanelManager.track(MixpanelEvents.savedCourses, {
      user_id: user_id,
      type: type,
    });
    MixpanelManager.track(MixpanelEvents.viewCourseContent, { content_type: 'saved_courses' });
    console.log('✅ Saved Courses Event Tracked');
  }, [type, user_id]);
  const queryFn = useCallback(
    ({ pageParam = 1 }) => getSeeCoursesByType({ page: pageParam, type }),
    [type]
  );

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ["savedCourses", type],
      queryFn,
      getNextPageParam: (lastPage) => {
        const nextPage = lastPage.data.current_page + 1;
        return nextPage <= lastPage.data.last_page ? nextPage : undefined;
      },
      enabled: !!type,      
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: false,      
      cacheTime: 0, 
      keepPreviousData: false, 
      onError: (error) => {
        toast.error(error.message);
      },
    });

  const courses = useMemo(
    () => data?.pages?.flatMap((page) => page.data.data) || [],
    [data?.pages]
  );

  const handleLoadMore = useCallback(() => {
    if (!isFetchingNextPage && hasNextPage) {
      MixpanelManager.track(MixpanelEvents.viewCourseContent, { action: 'load_more', type: 'saved_courses' });
      fetchNextPage();
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  if (isLoading) {
    return (
      <div className="cr-courses-grid">
        {Array.from({ length: initialShimmerCount }).map((_, index) => (
          <NewCourseCardShimmer width="100%" key={`saved-init-${index}`} />
        ))}
      </div>
    );
  }

  if (courses.length === 0) {
    return (
      <div className="centered-message-container">
        <div className="centered-message-text">
          <p className="fs-16 roboto-medium mb-1 text-muted">
            No Available Courses!
          </p>
          <p className="roboto-medium mb-1 text-muted">
            We're sorry, but there are currently no Saved Course available{" "}
            <br /> at the moment. Please check back later or explore other
            services.
          </p>
        </div>
      </div>
    );
  }

  return (
    <InfiniteScroll
      pageStart={0}
      loadMore={handleLoadMore}
      hasMore={Boolean(hasNextPage)}
      useWindow={true}
      threshold={350}
      loader={
        <div className="cr-courses-grid mt-3" key="saved-infinite-loader">
          {Array.from({ length: loaderShimmerCount }).map((_, index) => (
            <NewCourseCardShimmer width="100%" key={`saved-load-${index}`} />
          ))}
        </div>
      }
    >
      <div className="cr-courses-grid">
        {courses.map((course, index) => {
          MixpanelManager.track(MixpanelEvents.viewCourseContent, { course_id: course.id, action: 'view_saved' });
          return (
            <CourseCard 
              key={`${course.id}-${index}`}
              course={{ ...course, isSave: 1, is_saved: true, isSaved: true }} 
              width="100%"
            />
          );
        })}
      </div>
    </InfiniteScroll>
  );
};

export default React.memo(SavedCourses);